import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTimesheets } from '../hooks/useTimesheets';
import { getSignedTimesheets, deleteSignedTimesheet } from '../firebase/db';
import { onSnapshot, query, collection, orderBy } from 'firebase/firestore';
import { db } from '../firebase/index';
import { COLLECTIONS } from '../firebase/schema';
import { getMapUrl, formatCoordinates } from '../firebase/location';
import { format } from 'date-fns';
import './Timesheets.css';

const SignedTimesheetModal = ({ signedTs, onClose }) => {
  if (!signedTs) return null;
  const days = signedTs.days || [];
  const workEntries = signedTs.workEntries || {};
  const units = signedTs.units || {};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h3>Signed Timesheet</h3>
        
        <div className="modal-meta">
          <p><strong>Job:</strong> {signedTs.jobTitle}</p>
          <p><strong>Client:</strong> {signedTs.clientName}</p>
          <p><strong>Technician:</strong> {signedTs.technician}</p>
          <p><strong>Signed by:</strong> {signedTs.signerName} on {signedTs.signerDate}</p>
        </div>
        
        {signedTs.isMultiDay ? (
          <div className="modal-table-wrap">
            <table className="modal-table">
              <thead>
                <tr><th>Date</th><th>Work Carried Out</th><th>Units</th></tr>
              </thead>
              <tbody>
                {days.map((day, idx) => (
                  <tr key={idx}>
                    <td>{format(new Date(day), 'dd/MM/yyyy')}</td>
                    <td>{workEntries[idx] || '-'}</td>
                    <td>{units[idx] || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="modal-signature-section">
              <p><strong>Signature:</strong></p>
              {signedTs.signature ? (
                <img src={signedTs.signature} alt="Client signature" className="modal-sig-large" />
              ) : (
                <p className="modal-no-sig">No signature on file</p>
              )}
            </div>
          </div>
        ) : (
          <div className="single-day-view">
            <p><strong>Date:</strong> {signedTs.sheetDate}</p>
            <p><strong>Work:</strong> {signedTs.workCarriedOut || '-'}</p>
            <div className="modal-signature-section">
              <p><strong>Signature:</strong></p>
              {signedTs.signature ? (
                <img src={signedTs.signature} alt="Client signature" className="modal-sig-large" />
              ) : (
                <p className="modal-no-sig">No signature on file</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Location cell component for admin table
const LocationCell = ({ location, label }) => {
  if (!location || location.lat == null) return <span className="ts-no-location">—</span>;
  
  const mapUrl = getMapUrl(location.lat, location.lng);
  const coords = formatCoordinates(location.lat, location.lng);
  
  return (
    <a 
      href={mapUrl} 
      target="_blank" 
      rel="noopener noreferrer"
      className="ts-location-link"
      title={`${label} location: ${coords} (±${Math.round(location.accuracy || 0)}m)`}
    >
      📍 {coords}
    </a>
  );
};

const Timesheets = () => {
  const { user, profile, isAdmin } = useAuth();
  const { timesheets, loading, removeTimesheet } = useTimesheets();
  const navigate = useNavigate();
  const [signedTimesheets, setSignedTimesheets] = useState([]);
  const [signedLoading, setSignedLoading] = useState(false);
  const [selectedSigned, setSelectedSigned] = useState(null);

  const handleDeleteSigned = async (id) => {
    if (!window.confirm('Delete this signed timesheet? This cannot be undone.')) return;
    const res = await deleteSignedTimesheet(id);
    if (res.error) {
      alert('Error deleting: ' + res.error);
    } else {
      setSignedTimesheets(prev => prev.filter(st => st.id !== id));
    }
  };

  const handleDeleteTimesheet = async (id) => {
    if (!window.confirm('Delete this timesheet entry?')) return;
    const res = await removeTimesheet(id);
    if (res.error) {
      alert('Error deleting: ' + res.error);
    }
  };

  const totalHours = timesheets.reduce((sum, ts) => sum + (ts.hours || 0), 0);

  useEffect(() => {
    setSignedLoading(true);
    
    // Real-time listener for signed timesheets
    const q = query(
      collection(db, COLLECTIONS.signedTimesheets),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log('[Timesheets] Real-time update:', data.length, 'signed timesheets');
      // For non-admin, filter to only show their own signed timesheets
      const filtered = isAdmin
        ? data
        : data.filter((st) => st.createdBy === user?.uid || st.technician?.includes(profile?.displayName || ''));
      setSignedTimesheets(filtered);
      setSignedLoading(false);
    }, (err) => {
      console.error('[Timesheets] Signed timesheet listener error:', err);
      // Fallback: fetch without real-time listener
      getSignedTimesheets().then((data) => {
        const filtered = isAdmin
          ? data
          : data.filter((st) => st.createdBy === user?.uid || st.technician?.includes(profile?.displayName || ''));
        setSignedTimesheets(filtered);
        setSignedLoading(false);
      });
    });
    
    return () => unsubscribe();
  }, [isAdmin, user, profile]);

  const findSignedForJob = (jobId) => {
    return signedTimesheets.find((st) => st.jobId === jobId);
  };

  return (
    <div className="page timesheets">
      <h2>Timesheets</h2>

      <div className="ts-summary">
        <div className="ts-stat">
          <span className="ts-stat-num">{signedTimesheets.length}</span>
          <span className="ts-stat-label">Signed</span>
        </div>
        <div className="ts-stat">
          <span className="ts-stat-num">{timesheets.length}</span>
          <span className="ts-stat-label">Clock In/Out</span>
        </div>
        <div className="ts-stat">
          <span className="ts-stat-num">{totalHours.toFixed(1)}</span>
          <span className="ts-stat-label">Total Hours</span>
        </div>
      </div>

      {/* ─── Signed Timesheets Section (Primary / Mandatory) ─── */}
      <div className="ts-section signed-section">
        <h3>📋 Signed Timesheets</h3>
        <p className="ts-section-sub">Client-signed timesheets — mandatory for completed jobs</p>
        
        {signedLoading ? (
          <p className="ts-loading">Loading signed timesheets...</p>
        ) : signedTimesheets.length === 0 ? (
          <p className="empty">No signed timesheets yet.</p>
        ) : (
          <div className="signed-list">
            {signedTimesheets.map((st) => (
              <div key={st.id} className="signed-card" onClick={() => setSelectedSigned(st)}>
                <div className="signed-card-header">
                  <p><strong>{st.jobTitle}</strong></p>
                  <span className="signed-badge">
                    {st.signature ? '✓ Signed' : '✗ No signature'}
                  </span>
                </div>
                <p>{st.clientName} • {st.technician}</p>
                <p className="signed-date">{st.createdAt ? format(new Date(st.createdAt?.seconds ? st.createdAt.seconds * 1000 : st.createdAt), 'dd/MM/yyyy HH:mm') : ''}</p>
                {st.jobId && (
                  <div className="signed-card-actions" style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      className="ts-link-btn"
                      style={{ fontSize: '11px', padding: '3px 8px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/jobs/${st.jobId}`, { state: { returnTo: '/timesheets' } });
                      }}
                    >
                      View Job
                    </button>
                    {isAdmin && (
                      <button
                        className="ts-delete-btn"
                        style={{ fontSize: '11px', padding: '3px 8px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSigned(st.id);
                        }}
                        title="Delete signed timesheet"
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Clock In / Clock Out Timesheets (Optional) ─── */}
      <div className="ts-section clock-section">
        <h3>⏱️ Clock In / Clock Out Timesheets</h3>
        <p className="ts-section-sub">Optional worker timesheets — track hours</p>
        
        {loading ? (
          <p className="ts-loading">Loading...</p>
        ) : timesheets.length === 0 ? (
          <p className="empty">No clock-in/clock-out entries yet.</p>
        ) : (
          <div className="ts-table-wrapper">
            <table className="ts-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Clock In → Out</th>
                  <th>Hrs</th>
                  <th>Worker</th>
                  <th>Job</th>
                  {isAdmin && <th>Location (In → Out)</th>}
                  <th>Notes</th>
                  {isAdmin && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {timesheets.map((ts) => {
                  const signed = findSignedForJob(ts.jobId);
                  return (
                    <tr key={ts.id}>
                      <td>{format(new Date(ts.clockIn), 'dd/MM/yyyy')}</td>
                      <td>
                        {format(new Date(ts.clockIn), 'HH:mm')} — {format(new Date(ts.clockOut), 'HH:mm')}
                      </td>
                      <td>{ts.hours?.toFixed(1)}</td>
                      <td>{ts.workerName || profile?.displayName || 'You'}</td>
                      <td>
                        <div className="ts-actions">
                          {ts.jobId ? (
                            <button
                              className="ts-link-btn"
                              onClick={() => navigate(`/jobs/${ts.jobId}`, { state: { returnTo: '/timesheets' } })}
                            >
                              View Job
                            </button>
                          ) : (
                            'N/A'
                          )}
                          {signed && (
                            <button
                              className="ts-view-btn"
                              onClick={() => setSelectedSigned(signed)}
                            >
                              View Timesheet
                            </button>
                          )}
                        </div>
                      </td>
                      {isAdmin && (
                        <td>
                          <div className="ts-location-cell">
                            <LocationCell location={ts.clockInLocation} label="Clock In" />
                            <span className="ts-location-separator">→</span>
                            <LocationCell location={ts.clockOutLocation} label="Clock Out" />
                          </div>
                        </td>
                      )}
                      <td className="ts-note-cell">{ts.notes || '-'}</td>
                      {isAdmin && (
                        <td>
                          <button
                            className="ts-delete-btn"
                            onClick={() => handleDeleteTimesheet(ts.id)}
                            title="Delete timesheet"
                          >
                            🗑️
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedSigned && (
        <SignedTimesheetModal signedTs={selectedSigned} onClose={() => setSelectedSigned(null)} />
      )}
    </div>
  );
};

export default Timesheets;
